import { useRef, useState } from 'react';
import { FormInstance } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { paths } from '@app/constants';
import { showDefaultErrorToast, useToggleState } from '@app/utils';
import {
  useCreateQuestionDependency,
  useDeleteDependency,
  useDeleteQuestion,
  useEditDependency,
  useQuestionDependencies,
  useQuestionDetails,
} from '@data/hooks';
import {
  QuestionDependencyBody,
  QuestionDependencyView,
  QuestionView,
  VisibilityType,
} from '@data/sdk/match';
import { Namespace } from '@data/utils/enums';
import { antdNotification } from '@ui/components';
import { QuestionFormValues } from '../../../hooks';

export const useDependency = ({
  editQuestionMode,
  questionForm,
  handleEditQuestion,
}: {
  editQuestionMode: boolean;
  handleEditQuestion: (v: QuestionFormValues) => void;
  questionForm: FormInstance<QuestionFormValues>;
}) => {
  const { t } = useTranslation([Namespace.ADMIN]);
  const navigate = useNavigate();
  const { questionId = '' } = useParams<{ questionId: string }>();

  const dependencyRef = useRef<HTMLDivElement>(null);

  const [createNewDependency, setCreateNewDependency] = useState(false);
  const [dependencyToEdit, setDependencyToEdit] = useState<QuestionDependencyView | null>(null);
  const [showConfirmationModal, toggleConfirmationModal] = useToggleState(false);
  const [dependencyFromSimilarQuestion, setDependencyFromSimilarQuestion] =
    useState<QuestionView | null>(null);

  const { data: question } = useQuestionDetails(questionId);
  const { data: dependencies } = useQuestionDependencies(questionId);

  const { mutate: deleteQuestion } = useDeleteQuestion();
  const { mutate: editDependency } = useEditDependency();
  const { mutate: deleteDependency } = useDeleteDependency();
  const { mutate: createDependency } = useCreateQuestionDependency();

  const saveDependency = async (formValues: QuestionDependencyBody) => {
    if (dependencyToEdit) {
      editDependency(
        {
          dependencyId: dependencyToEdit.id,
          values: { parameters: formValues.parameters },
        },
        {
          onSuccess: () => setDependencyToEdit(null),
          onError: (err) => {
            antdNotification.error({
              title: t(`patient:beErrors.${err.response?.data.message}`, {
                defaultValue: t('patient:beErrors.unexpectedError'),
                item: t('question_page.details.dependencies.dependency'),
              }),
            });
          },
        },
      );
    } else {
      createDependency(
        { id: questionId, values: formValues },
        {
          onSuccess: () => setCreateNewDependency(false),
          onError: (err) => {
            antdNotification.error({
              title: t(`patient:beErrors.${err.response?.data.message}`, {
                defaultValue: t('patient:beErrors.unexpectedError'),
                item: t('question_page.details.dependencies.dependency'),
              }),
            });
          },
        },
      );
    }
  };

  const saveQuestion = async () => handleEditQuestion(questionForm.getFieldsValue(true));

  const handleSaveDependency = async (formValues: QuestionDependencyBody) => {
    if (editQuestionMode) {
      await saveQuestion();
      saveDependency(formValues);
    } else {
      saveDependency(formValues);
    }
  };

  const handleDeleteDependency = () => {
    if (dependencyToEdit)
      deleteDependency(dependencyToEdit?.id, {
        onSuccess: () => {
          if (
            question?.visibilityType === VisibilityType.Hidden &&
            dependencies?.sources?.length === 1
          ) {
            deleteQuestion(question.id, {
              onSuccess: () => navigate(`/${paths.app}/${paths.questions_library}`),
            });
          }
          setDependencyToEdit(null);
          toggleConfirmationModal();
        },
        onError: showDefaultErrorToast,
      });
  };

  const scrollToDependency = () => {
    dependencyRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const createDependencyFromSimilarQuestion = (similarQuestion: QuestionView) => {
    setCreateNewDependency(true);
    setDependencyFromSimilarQuestion(similarQuestion);
    scrollToDependency();
  };

  const closeDependencyForm = () => {
    setCreateNewDependency(false);
    setDependencyFromSimilarQuestion(null);
  };

  return {
    dependencyRef,
    dependencyToEdit,
    createNewDependency,
    showConfirmationModal,
    dependencyFromSimilarQuestion,
    setDependencyToEdit,
    closeDependencyForm,
    handleSaveDependency,
    setCreateNewDependency,
    handleDeleteDependency,
    toggleConfirmationModal,
    createDependencyFromSimilarQuestion,
  };
};
